import type {
  CorridorOption,
  Interest,
  MonthAdvice,
  Itinerary,
  TravelMode,
  UiLang,
  Recommendation,
  NearbyStatus,
  NearbyStop,
  Origin,
  Stop,
  StopLine,
  TripForm,
  PlaceTrip,
  ExplorePlace,
  VehicleKind,
} from "./types";

import { contentLang } from "./i18n";

const BASE = "/api";

/*
 * Grensesnittspråket oversettes til innholdsspråk her, én gang, i inngangen.
 *
 * Grunnen til at det skjer her og ikke hos kalleren: API-et kan bare svare på
 * de språkene innholdet finnes på, og et kall med `lang=de` ville fått 400 for
 * et språk serveren ikke kjenner. Å legge oversettelsen på hvert kallsted er
 * seks steder å huske den samme regelen — og ett glemt sted er en feil som
 * bare vises for tyske brukere.
 */
const forServer = (lang: UiLang) => contentLang(lang);

async function request<T>(path: string, lang: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      // Serveren oversetter advarsler og feilmeldinger etter denne.
      "Accept-Language": lang,
      ...init?.headers,
    },
  });

  if (!response.ok) {
    let message = `Serveren svarte ${response.status}`;
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // Behold statuskoden som feilmelding.
    }
    throw new Error(message);
  }

  return (await response.json()) as T;
}

export function fetchCorridors(
  lang: UiLang,
): Promise<{
  corridors: CorridorOption[];
  origins?: Origin[];
  seasons?: Partial<Record<Interest, number[]>>;
  /** Månedene hvert kjøretøy kan brukes. Se `VEHICLE_SEASON` på serveren. */
  vehicleSeasons?: Partial<Record<VehicleKind, number[]>>;
  /** Om meldingsskjemaet er satt opp. Uten det rendres et skjema som alltid feiler. */
  contactEnabled?: boolean;
}> {
  const server = forServer(lang);
  return request(`/corridors?lang=${server}`, server);
}

/**
 * En melding til den som har laget appen.
 *
 * Adressen står ikke her, og skal ikke det: den bor i en miljøvariabel på
 * serveren, og et vellykket svar sier ingenting om hvor meldingen tok veien.
 * Se api/src/services/contact.ts.
 *
 * `company` er honningkrukka. Feltet er skjult i skjemaet, så et menneske
 * fyller det aldri ut — og en robot som fyller ut alt den finner, avslører seg.
 * Det står i typen framfor bare i markup fordi det ellers er nettopp den
 * slags felt noen fjerner i god tro.
 */
export interface ContactInput {
  name: string;
  replyTo: string;
  message: string;
  company?: string;
}

export function sendContactMessage(input: ContactInput, lang: UiLang): Promise<{ ok: true }> {
  const server = forServer(lang);
  return request("/contact", server, { method: "POST", body: JSON.stringify(input) });
}

/**
 * Når man bør komme, ut fra hva man kommer for.
 *
 * Egen rute og ikke en del av korridorsvaret, fordi den avhenger av knapper
 * som trykkes etter at siden er lastet.
 */
export function fetchMonthAdvice(
  interests: Interest[],
  lang: UiLang,
  corridorId?: string,
): Promise<MonthAdvice> {
  const server = forServer(lang);
  const query = new URLSearchParams({ interests: interests.join(","), lang: server });
  if (corridorId) query.set("rute", corridorId);
  return request(`/month-advice?${query.toString()}`, server);
}

/** Alle stedene, lett nok til å tegne en mosaikk av dem. */
export function fetchPlaces(lang: UiLang): Promise<{ places: ExplorePlace[] }> {
  const server = forServer(lang);
  return request(`/places?lang=${server}`, server);
}

/**
 * Turen til ett sted. Uten `stopId` svarer den med steder å velge mellom.
 *
 * To utganger fra samme endepunkt, fordi det er to trinn i det samme
 * spørsmålet: «hvor vil du?» og så «hvor lenge?». Se services/placeTrip.ts.
 */
export function fetchHeadlinePlaces(lang: UiLang): Promise<{ places: Stop[] }> {
  const server = forServer(lang);
  return request(`/place-trip?lang=${server}`, server);
}

export function fetchPlaceTrip(
  stopId: string,
  days: number,
  pace: string,
  lang: UiLang,
  fromStopId?: string,
): Promise<PlaceTrip> {
  const server = forServer(lang);
  const query = new URLSearchParams({ stop: stopId, days: String(days), pace, lang: server });
  if (fromStopId) query.set("from", fromStopId);
  return request(`/place-trip?${query.toString()}`, server);
}

/** Rangerte ruteforslag ut fra hva den reisende er ute etter. */
export function fetchRecommendations(
  interests: Interest[],
  days: number,
  month: number,
  lang: UiLang,
): Promise<{ recommendations: Recommendation[] }> {
  const query = new URLSearchParams({
    interests: interests.join(","),
    days: String(days),
    month: String(month),
    lang,
  });
  return request(`/recommend?${query}`, forServer(lang));
}

/**
 * Linjene som stopper ved ett stopp, med intervall.
 *
 * Eget kall, hentet når stoppkortet åpnes. Ett Entur-oppslag per stopp i planen
 * ville spist store deler av kvoten på underforespørsler, for noe man leser om
 * ett stopp av gangen.
 */
export function fetchLines(
  stopId: string,
  lang: UiLang,
): Promise<{
  stopId: string;
  name: string;
  lines: StopLine[];
  nearby: NearbyStop[];
  nearbyStatus: NearbyStatus;
}> {
  const server = forServer(lang);
  return request(`/lines?stop=${encodeURIComponent(stopId)}&lang=${server}`, server);
}

export function fetchStops(
  corridorId: string,
  lang: UiLang,
): Promise<{ corridorId: string; stops: Stop[]; modeNotes: Record<TravelMode, string> }> {
  const server = forServer(lang);
  return request(`/stops?corridor=${encodeURIComponent(corridorId)}&lang=${server}`, server);
}

export function fetchItinerary(
  form: TripForm,
  lang: UiLang,
  signal?: AbortSignal,
): Promise<Itinerary> {
  const server = forServer(lang);
  return request("/itinerary", server, {
    method: "POST",
    // Serveren validerer `lang` mot sine egne to; `de` ville gitt 400.
    body: JSON.stringify({ ...form, lang: server, startDate: form.startDate || undefined }),
    signal,
  });
}

export interface TripFromText {
  form: TripForm;
  /** Modellens egen setning om hva den forsto. Vises før planen lages. */
  understood: string;
  /** Feltene teksten ikke sa noe om, og som derfor er standardverdier. */
  assumed: string[];
}

/**
 * Fyller ut skjemaet ut fra noen setninger fritekst.
 *
 * Merk at dette ikke gir en plan. Det gir et utfylt skjema, som går videre til
 * `fetchItinerary` som ellers — det er én vei til en reiseplan, og fritekst er
 * en annen måte å fylle ut skjemaet på. Da kan brukeren se hva som ble forstått
 * og rette det før planen lages, i stedet for å få en ferdig plan og lure på
 * hvor den kom fra.
 *
 * Kaster `AiUnavailable` når språkmodellen ikke svarte. Det er en helt vanlig
 * utgang — kvoten er delt og tar slutt — og skjemaet gir den samme planen.
 */
export async function planFromText(text: string, lang: UiLang): Promise<TripFromText> {
  const server = forServer(lang);
  const response = await fetch(`${BASE}/plan-from-text`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Accept-Language": server },
    body: JSON.stringify({ text, lang: server }),
  });

  const body = (await response.json().catch(() => ({}))) as {
    error?: string;
    aiUnavailable?: boolean;
  } & Partial<TripFromText>;

  if (!response.ok) {
    if (body.aiUnavailable) throw new AiUnavailable(body.error ?? "");
    throw new Error(body.error ?? `Serveren svarte ${response.status}`);
  }

  return body as TripFromText;
}

/** Språkmodellen er ikke tilgjengelig. Skjemaet virker fortsatt. */
export class AiUnavailable extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AiUnavailable";
  }
}
